import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getHierarchyNodes } from "../services/api";
import { getTaxonomyHierarchyConfigByEntityType } from "../utils/taxonomyHierarchyConfigs";

/** Session cache so reopening Add dialog restores tree without refetching. */
const TREE_SESSION_CACHE = new Map();

const cacheKeyForType = (entityType) =>
  String(entityType || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-")
    .replace(/\s+/g, "-");

const readTreeSession = (entityType) => {
  const key = cacheKeyForType(entityType);
  if (!key || !TREE_SESSION_CACHE.has(key)) return null;
  const cached = TREE_SESSION_CACHE.get(key);
  return {
    nodes: Array.isArray(cached.nodes) ? cached.nodes.map((n) => ({ ...n })) : [],
    loadedParentIds: new Set(cached.loadedParentIds || [null]),
    expandedIds: new Set(cached.expandedIds || []),
  };
};

const writeTreeSession = (entityType, { nodes, loadedParentIds, expandedIds }) => {
  const key = cacheKeyForType(entityType);
  if (!key) return;
  TREE_SESSION_CACHE.set(key, {
    nodes: Array.isArray(nodes) ? nodes.map((n) => ({ ...n })) : [],
    loadedParentIds: Array.from(loadedParentIds || []),
    expandedIds: Array.from(expandedIds || []),
  });
};

/** Drop session tree UI cache (e.g. after creating a taxonomy node). */
export const clearHierarchyTreeSessionCache = (entityType = null) => {
  if (!entityType) {
    TREE_SESSION_CACHE.clear();
    return;
  }
  TREE_SESSION_CACHE.delete(cacheKeyForType(entityType));
};

/**
 * Lazy multi-select taxonomy tree for the relationship Add dialog.
 * Selection is controlled by the parent via selectedIds / onToggle.
 * Tree data is cached for the browser session after first load.
 */
const HierarchyMultiSelectTree = ({
  entityType,
  selectedIds,
  onToggle,
  excludeIds = null,
  searchQuery = "",
}) => {
  const cfg = getTaxonomyHierarchyConfigByEntityType(entityType);
  const typeKey = cfg?.entityType || cacheKeyForType(entityType);

  const initial = useMemo(() => readTreeSession(typeKey), [typeKey]);
  const [nodes, setNodes] = useState(() => initial?.nodes || []);
  const [expandedIds, setExpandedIds] = useState(
    () => initial?.expandedIds || new Set()
  );
  const [loadedParentIds, setLoadedParentIds] = useState(
    () => initial?.loadedParentIds || new Set()
  );
  const [loadingRoots, setLoadingRoots] = useState(
    () => !(initial?.loadedParentIds?.has(null))
  );
  const [loadingChildId, setLoadingChildId] = useState(null);
  const [error, setError] = useState("");
  const hydratedRef = useRef(Boolean(initial));

  const excluded = useMemo(() => {
    const set = new Set();
    if (excludeIds) {
      for (const id of excludeIds) {
        if (id) set.add(String(id));
      }
    }
    return set;
  }, [excludeIds]);

  // Persist tree session whenever structure changes
  useEffect(() => {
    if (!typeKey) return;
    if (!loadedParentIds.has(null) && nodes.length === 0) return;
    writeTreeSession(typeKey, { nodes, loadedParentIds, expandedIds });
  }, [typeKey, nodes, loadedParentIds, expandedIds]);

  const loadRoots = useCallback(async () => {
    if (!cfg?.entityType) return;
    const cached = readTreeSession(cfg.entityType);
    if (cached && cached.loadedParentIds.has(null)) {
      setNodes(cached.nodes);
      setLoadedParentIds(cached.loadedParentIds);
      setExpandedIds(cached.expandedIds);
      setLoadingRoots(false);
      hydratedRef.current = true;
      return;
    }
    setLoadingRoots(true);
    setError("");
    try {
      // Use cached API responses (forceRefresh=false) after first fetch.
      const roots = await getHierarchyNodes(cfg.entityType, null, false);
      setNodes(roots || []);
      setLoadedParentIds(new Set([null]));
      setExpandedIds(new Set());
      hydratedRef.current = true;
    } catch (e) {
      setError(e.message || `Failed to load ${cfg.entityLabelPlural}`);
      setNodes([]);
    } finally {
      setLoadingRoots(false);
    }
  }, [cfg?.entityType, cfg?.entityLabelPlural]);

  useEffect(() => {
    if (hydratedRef.current && loadedParentIds.has(null)) {
      setLoadingRoots(false);
      return;
    }
    loadRoots();
  }, [loadRoots, loadedParentIds]);

  const loadChildren = async (parentId) => {
    if (!cfg?.entityType) return;
    if (loadedParentIds.has(parentId)) return;
    setLoadingChildId(parentId);
    try {
      const children = await getHierarchyNodes(cfg.entityType, parentId, false);
      setNodes((prev) => {
        const byId = new Map(prev.map((n) => [n.id, n]));
        (children || []).forEach((c) => byId.set(c.id, c));
        return Array.from(byId.values());
      });
      setLoadedParentIds((prev) => new Set(prev).add(parentId));
    } catch (e) {
      setError(e.message || "Failed to load children");
    } finally {
      setLoadingChildId(null);
    }
  };

  const toggleExpand = async (nodeId) => {
    if (expandedIds.has(nodeId)) {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        next.delete(nodeId);
        return next;
      });
      return;
    }
    await loadChildren(nodeId);
    setExpandedIds((prev) => new Set(prev).add(nodeId));
  };

  const nodeById = useMemo(() => {
    const map = {};
    nodes.forEach((n) => {
      map[n.id] = n;
    });
    return map;
  }, [nodes]);

  const pathLabel = useCallback(
    (node) => {
      const parts = [];
      let cur = node;
      let guard = 0;
      while (cur && guard < 12) {
        parts.unshift(cur.name || cur.id);
        if (!cur.parent || !nodeById[cur.parent]) break;
        cur = nodeById[cur.parent];
        guard += 1;
      }
      return parts.join(" › ");
    },
    [nodeById]
  );

  const childrenByParent = useMemo(() => {
    const map = {};
    nodes.forEach((n) => {
      const key = n.parent || null;
      if (!map[key]) map[key] = [];
      map[key].push(n);
    });
    Object.values(map).forEach((list) =>
      list.sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", "en", { sensitivity: "base" })
      )
    );
    return map;
  }, [nodes]);

  const roots = childrenByParent[null] || nodes.filter((n) => !n.parent);
  const searchLower = String(searchQuery || "").trim().toLowerCase();

  const searchMatches = useMemo(() => {
    if (!searchLower) return null;
    return nodes.filter(
      (n) =>
        !excluded.has(String(n.id)) &&
        (n.name || "").toLowerCase().includes(searchLower)
    );
  }, [nodes, searchLower, excluded]);

  const renderNode = (node, depth = 0) => {
    const id = String(node.id);
    if (excluded.has(id)) return null;
    const isSelected = selectedIds?.has?.(node.id) || selectedIds?.has?.(id);
    const isExpanded = expandedIds.has(node.id);
    const childrenLoaded = loadedParentIds.has(node.id);
    const childList = (childrenByParent[node.id] || []).filter(
      (c) => !excluded.has(String(c.id))
    );
    const mayHaveChildren = childrenLoaded ? childList.length > 0 : true;
    const isLoadingChildren = loadingChildId === node.id;

    return (
      <div key={node.id} className="mb-1" style={{ marginLeft: depth * 14 }}>
        <div
          className={`flex items-start gap-2 rounded-lg border px-2 py-1.5 ${
            isSelected
              ? "bg-blue-900/40 border-blue-500"
              : "bg-gray-700/60 border-gray-600 hover:border-gray-500"
          }`}
        >
          {mayHaveChildren ? (
            <button
              type="button"
              onClick={() => toggleExpand(node.id)}
              disabled={isLoadingChildren}
              className="mt-0.5 w-5 h-5 flex-shrink-0 rounded bg-gray-600 hover:bg-gray-500 text-gray-200 flex items-center justify-center disabled:opacity-60"
              aria-label={isExpanded ? "Collapse" : "Expand"}
            >
              {isLoadingChildren ? (
                <span className="w-2.5 h-2.5 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg
                  className={`w-3 h-3 transition-transform ${
                    isExpanded ? "rotate-90" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              )}
            </button>
          ) : (
            <div className="w-5 flex-shrink-0" />
          )}

          <button
            type="button"
            onClick={() => onToggle?.(node)}
            className="flex-1 min-w-0 text-left flex items-start gap-2"
          >
            <input
              type="checkbox"
              readOnly
              checked={Boolean(isSelected)}
              className="mt-0.5 h-4 w-4 rounded border-gray-500 text-blue-600 pointer-events-none"
              aria-hidden
            />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-white truncate">
                {node.name || node.id}
              </div>
              {depth > 0 || node.parent ? (
                <div className="text-[10px] text-gray-500 truncate mt-0.5">
                  {pathLabel(node)}
                </div>
              ) : null}
            </div>
          </button>
        </div>
        {isExpanded && childrenLoaded
          ? childList.map((child) => renderNode(child, depth + 1))
          : null}
      </div>
    );
  };

  if (!cfg) {
    return (
      <p className="text-sm text-gray-400 text-center py-6">
        Hierarchy is not available for this type.
      </p>
    );
  }

  if (loadingRoots) {
    return (
      <div className="flex items-center justify-center gap-2 py-8 text-gray-400 text-sm">
        <span className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        {cfg.loadingLabel}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3 rounded bg-red-900/30 border border-red-700 text-red-300 text-sm">
        {error}
      </div>
    );
  }

  if (searchMatches) {
    if (searchMatches.length === 0) {
      return (
        <p className="text-sm text-gray-400 text-center py-6">
          No loaded nodes match your search. Expand the tree to load more, then
          search again.
        </p>
      );
    }
    return (
      <div className="space-y-1">
        <p className="text-xs text-gray-500 mb-1">
          Matches among loaded nodes
        </p>
        {searchMatches.map((node) => {
          const isSelected =
            selectedIds?.has?.(node.id) || selectedIds?.has?.(String(node.id));
          return (
            <button
              key={node.id}
              type="button"
              onClick={() => onToggle?.(node)}
              className={`w-full text-left rounded-lg border px-3 py-2 flex items-start gap-2 ${
                isSelected
                  ? "bg-blue-900/40 border-blue-500"
                  : "bg-gray-700/60 border-gray-600"
              }`}
            >
              <input
                type="checkbox"
                readOnly
                checked={Boolean(isSelected)}
                className="mt-0.5 h-4 w-4 rounded border-gray-500 text-blue-600 pointer-events-none"
                aria-hidden
              />
              <div className="min-w-0">
                <div className="text-sm text-white font-medium">
                  {node.name || node.id}
                </div>
                <div className="text-[10px] text-gray-500 truncate">
                  {pathLabel(node)}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    );
  }

  if (roots.length === 0) {
    return (
      <p className="text-sm text-gray-400 text-center py-6">{cfg.emptyLabel}</p>
    );
  }

  return <div>{roots.map((node) => renderNode(node, 0))}</div>;
};

export default HierarchyMultiSelectTree;
